export type ParkOscillator = {
  type: OscillatorType;
  frequency: {
    setValueAtTime(value: number, time: number): void;
  };
  connect(node: unknown): void;
  start(when?: number): void;
  stop(when?: number): void;
  disconnect(): void;
  onended: ((this: AudioScheduledSourceNode, ev: Event) => unknown) | (() => void) | null;
};

export type ParkGain = {
  gain: {
    setValueAtTime(value: number, time: number): void;
    linearRampToValueAtTime(value: number, time: number): void;
    exponentialRampToValueAtTime(value: number, time: number): void;
  };
  connect(node: unknown): void;
  disconnect(): void;
};

export type ParkFilter = {
  type: BiquadFilterType;
  frequency: {
    setValueAtTime(value: number, time: number): void;
  };
  Q: {
    setValueAtTime(value: number, time: number): void;
  };
  connect(node: unknown): void;
  disconnect(): void;
};

export type ParkBuffer = {
  getChannelData(channel: number): Float32Array;
};

export type ParkBufferSource = {
  buffer: ParkBuffer | null;
  loop: boolean;
  connect(node: unknown): void;
  start(when?: number): void;
  stop(when?: number): void;
  disconnect(): void;
  onended: ((this: AudioScheduledSourceNode, ev: Event) => unknown) | (() => void) | null;
};

export type ParkAudioContext = {
  currentTime: number;
  sampleRate: number;
  state: string;
  destination: object;
  resume?: () => Promise<void> | void;
  close?: () => Promise<void> | void;
  createOscillator(): ParkOscillator;
  createGain(): ParkGain;
  createBiquadFilter(): ParkFilter;
  createBuffer(channels: number, length: number, sampleRate: number): ParkBuffer;
  createBufferSource(): ParkBufferSource;
};

type AudioContextCtor = new () => ParkAudioContext;

export function createBrowserAudioContext(): ParkAudioContext | null {
  const global = globalThis as typeof globalThis & {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const Ctor = global.AudioContext ?? global.webkitAudioContext;
  if (!Ctor) {
    return null;
  }
  try {
    return new Ctor();
  } catch {
    return null;
  }
}

export function isAudioContextLocked(state: string): boolean {
  return state === 'suspended' || state === 'interrupted';
}

export function resumeAudioContext(ctx: ParkAudioContext | null): void {
  if (!ctx || !isAudioContextLocked(ctx.state) || typeof ctx.resume !== 'function') {
    return;
  }
  void Promise.resolve(ctx.resume()).catch(() => undefined);
}
